import React, { ReactNode, RefObject } from "react";
import { useTranslation } from 'react-i18next';
import styles from "../data-table.module.scss";
import { TableData } from "../../../../types/table-filter-types";
import Loader from "../../cirlcles-loader/circles-loader";
import TableRow from "../table-row/table-row";
import SearchBar from "../../search-bar/search-bar";
import Button from "../../button/button";
import { Filters } from "../../filters/filters";
import Checkbox from "../../checkbox/checkbox";
import { TableFilters } from "../../../../utils/filters-for-table";

interface TableProps {
  children: ReactNode;
  data: TableData;
  isLoading: boolean;
  rowHeight: number;
  dateTemplate?: string;
  tableName: string;
  tableIcon: string;
  currentPage: number;
  pageSize: number;
  totalDataSize: number;
  handlePageChange: (page: number) => void;
  mainRef: RefObject<HTMLDivElement>;
  openedPopup: string;
  setOpenedPopup: (popupField: string) => void;
  mainTitleRef: RefObject<HTMLDivElement>;
  filtersTitleRef: RefObject<HTMLElement>;
  tableRef: RefObject<HTMLTableElement>;
  tableHeaderRef: RefObject<HTMLTableSectionElement>;
  paginationRef: RefObject<HTMLDivElement>;
  debounceMs: number;
  idField: string;
  searchAndFiltersRef: RefObject<HTMLDivElement>;
  activeFiltersRef: RefObject<HTMLDivElement>;
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSearch?: () => void; // Функция, вызываемая при нажатии Enter
  searchValue: string;
  filters: TableFilters;
  setFilters: (arg: TableFilters) => void;
  setSearchValue: (arg: string) => void;
  headerContent?: ReactNode;
  visibleFilter: boolean;
  selected?: number[];
  addSelected?: (ids: number[]) => void;
  deleteSelected?: (ids: number[]) => void;
  deleteAllSelected?: () => void;
  placeholderSearch: string;
  renderRowMenu?: (rowData: Record<string, any>) => React.ReactNode;
  secondLineOpacity?: boolean;
  visibleSelect?: boolean;
  onRowClick?: (rowData: Record<string, any>) => void;
  getRowColor?: (rowData: Record<string, any>) => string | undefined;
}

const Table: React.FC<TableProps> = ({
  children,
  data,
  isLoading,
  dateTemplate,
  tableName,
  tableIcon,
  rowHeight,
  pageSize,
  mainRef,
  mainTitleRef,
  tableRef,
  tableHeaderRef,
  debounceMs,
  idField,
  searchAndFiltersRef,
  activeFiltersRef,
  onSearchChange,
  onSearch,
  searchValue,
  filters,
  setSearchValue,
  setFilters,
  headerContent,
  visibleFilter,
  selected,
  addSelected,
  deleteSelected,
  deleteAllSelected,
  placeholderSearch,
  renderRowMenu,
  secondLineOpacity,
  visibleSelect,
  onRowClick,
  getRowColor,
}) => {
  const { t } = useTranslation();
  // Get IDs of rows currently displayed on the page
  const currentPageIds = data.map((item) => item[idField]);

  // Check if all rows on the current page are selected
  const allSelected = currentPageIds.every((id) => selected?.includes(id));

  const handleHeaderCheckboxChange = () => {
    if (!visibleSelect) return;
    if (allSelected) {
      deleteSelected?.(currentPageIds);
    } else {
      addSelected?.(currentPageIds);
    }
  };

  function normalizeCaption(caption: string) {
    return caption
        .split("\n")[0]
        .split("(")[0]
        .replace(/[^\w]/g, "");
  }

  return (
    <div className={styles.tableComponent} ref={mainRef}>
      <div className={styles.header}>
        <div className={styles.titleContainer} ref={mainTitleRef}>
          <img
            className={styles.titleIcon}
            src={tableIcon}
            alt={`${tableName} icon`}
          />
          <h1 className={styles.title}>{tableName}</h1>
        </div>
        {headerContent}
        <div className={styles.searchAndFilters} ref={searchAndFiltersRef}>
          <SearchBar
            searchValue={searchValue}
            onSearchChange={onSearchChange}
            onClearSearch={() => setSearchValue("")}
            onSearch={onSearch}
            placeholder={placeholderSearch}
            className={styles.searchField}
            searchFieldIcon={styles.searchFieldIcon}
          />
          {visibleFilter &&
              <Button type="outline" className={styles.filterBtn}>
                <span>{t('table.filter.button')}</span>
              </Button>
          }
        </div>
      </div>
      {Object.entries(filters).length > 0 && (
        <div className={styles.activeFilters} ref={activeFiltersRef}>
          <Filters tableMode={true} filters={filters} setFilters={setFilters} />
        </div>
      )}
      <div style={{ position: "relative" }}>
        {isLoading && (
          <div className={styles.loaderWrapper}>
            <Loader />
          </div>
        )}
        <div
          className={`${styles.table} ${renderRowMenu ? styles.withRowMenu : ""}`}
          ref={tableRef}
        >
          <div ref={tableHeaderRef} className={styles.thead}>
            <div className={styles.tr}>
              {visibleSelect && (
                  <div
                      style={{ width: "40px" }}
                      className={`${styles.firstColumn} ${styles.th}`}
                  >
                    <Checkbox
                        checked={allSelected}
                        onChange={handleHeaderCheckboxChange}
                        backgroundColor="#2E1A471A"
                    />
                  </div>
              )}
              {React.Children.map(children, (child) => {
                if (React.isValidElement(child)) {
                  if (child.props.caption.includes("\n")) {
                    let splited = child.props.caption.split("\n");

                    return (
                      <div
                        key={child.props.dataField}
                        className={`${styles.th} ${child.props.caption ? styles[normalizeCaption(child.props.caption)] : ''}`}
                        style={child.props.style}
                      >
                        {splited[0]}
                        <br />
                        {splited[1]}
                      </div>
                    );
                  }
                  return (
                    <div
                      key={child.props.dataField}
                      className={`${styles.th} ${child.props.caption ? styles[normalizeCaption(child.props.caption)] : ''}`}
                      style={child.props.style}
                    >
                      {child.props.caption}
                      {/* <button
                        onClick={() => {
                          setOpenedPopup(
                            openedPopup === child.props.dataField
                              ? ""
                              : child.props.dataField,
                          );
                        }}
                      >
                        <img src={filterIco} alt="Фильтр" />
                      </button> */}
                    </div>
                  );
                }
                return null;
              })}
              {renderRowMenu && <th></th>}
            </div>
          </div>
          <div
            style={{ height: pageSize * rowHeight, position: "relative" }}
            className={`${isLoading ? styles.blur : ""} ${styles.tbody}`}
          >
            {data.map((item, index) => (
              <TableRow
                key={index}
                id={item[idField]}
                data={item}
                dateTemplate={dateTemplate}
                style={{ height: rowHeight }}
                selected={selected}
                addSelected={addSelected}
                deleteSelected={deleteSelected}
                renderRowMenu={renderRowMenu}
                secondLineOpacity={secondLineOpacity}
                visibleSelect={visibleSelect}
                onRowClick={onRowClick}
                getRowColor={getRowColor}
              >
                {children}
              </TableRow>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Table;
